import { db } from '../firebase';
import { collection, query, where, getDocs, doc, getDoc } from '../firestore-compat';
import { ROLES as CONFIG_ROLES, hasPermission as checkPermission } from '../config/permissions';

/**
 * Papéis disponíveis no sistema
 * Sincronizado com src/config/permissions.js
 */
export const ROLES = CONFIG_ROLES;

/**
 * Re-exporta a verificação de permissão centralizada
 */
export const hasPermission = checkPermission;

/**
 * Helper para identificar o usuário Super Admin (Oliver Barea)
 * @param {string} email - Email do usuário atual
 * @returns {boolean} Se é super admin
 */
export const isSuperUser = (email) => {
    return email === 'alexandrebareaoliveira@gmail.com';
};


/**
 * Busca os papéis de um usuário pelo UID
 * @param {string} uid - UID do usuário
 * @returns {Promise<string[]>} Array de papéis do usuário
 */
export async function getUserRoles(uid) {
    try {
        const userDoc = await getDoc(doc(db, 'users', uid));
        if (userDoc.exists()) {
            return userDoc.data().roles || [];
        }

        // Fallback: Tentar buscar por campo 'uid' dentro da coleção se não achou pelo ID
        const q = query(collection(db, 'users'), where('uid', '==', uid));
        const snap = await getDocs(q);
        if (!snap.empty) {
            return snap.docs[0].data().roles || [];
        }

        return [];
    } catch (error) {
        console.error('Erro ao buscar roles do usuário:', error);
        return [];
    }
}

/**
 * Busca os papéis de um usuário pelo Email
 * @param {string} email - Email do usuário
 * @returns {Promise<string[]>} Array de papéis do usuário
 */
export async function getUserRolesByEmail(email) {
    try {
        if (!email) return [];
        const q = query(collection(db, 'users'), where('email', '==', email));
        const snap = await getDocs(q);
        if (!snap.empty) {
            return snap.docs[0].data().roles || [];
        }
        return [];
    } catch (error) {
        console.error('Erro ao buscar roles por email:', error);
        return [];
    }
}

/**
 * Busca dados completos de um usuário pelo UID
 * @param {string} uid - UID do usuário
 * @returns {Promise<Object|null>} Dados do usuário ou null
 */
export async function getUserData(uid) {
    try {
        const userDoc = await getDoc(doc(db, 'users', uid));
        if (userDoc.exists()) {
            return { id: userDoc.id, ...userDoc.data() };
        }
        return null;
    } catch (error) {
        console.error('Erro ao buscar dados do usuário:', error);
        return null;
    }
}

/**
 * Busca todos os usuários que possuem pelo menos um dos papéis especificados
 * @param {string[]} roles - Array de papéis para filtrar
 * @returns {Promise<Object[]>} Array de usuários
 */
export async function getUsersByRoles(roles) {
    try {
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('roles', 'array-contains-any', roles));
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
        console.error('Erro ao buscar usuários por roles:', error);
        return [];
    }
}

// Funções de verificação de Papel mantidas para compatibilidade legada
// Mas idealmente devemos migrar para hasPermission(userRoles, capability)

/**
 * Verifica se um usuário tem pelo menos um dos papéis especificados
 * @param {string[]} userRoles - Papéis do usuário
 * @param {string[]} requiredRoles - Papéis requeridos
 * @returns {boolean}
 */
export function hasAnyRole(userRoles, requiredRoles) {
    if (!userRoles || !Array.isArray(userRoles)) return false;
    return userRoles.some(role => requiredRoles.includes(role));
}

/**
 * Verifica se um usuário tem todos os papéis especificados
 * @param {string[]} userRoles - Papéis do usuário
 * @param {string[]} requiredRoles - Papéis requeridos
 * @returns {boolean}
 */
export function hasAllRoles(userRoles, requiredRoles) {
    if (!userRoles || !Array.isArray(userRoles)) return false;
    return requiredRoles.every(role => userRoles.includes(role));
}

/**
 * Determina a agenda de destino quando um usuário aceita um convite
 * @param {string[]} userRoles - Papéis do usuário que está aceitando
 * @param {string} sourceCollection - Coleção de origem do convite
 * @returns {string} Nome da coleção de destino
 */
export function getTargetAgenda(userRoles, sourceCollection) {
    // Se o usuário é auditor da qualidade, eventos vão para auditorias
    if (hasAnyRole(userRoles, [ROLES.AUDITOR_QUALIDADE])) {
        return 'auditorias';
    }

    // Se o evento veio da Agenda Direção, mantém lá
    if (sourceCollection === 'agendaDirecao') {
        return 'agendaDirecao';
    }

    // Caso contrário, vai para agenda pessoal
    return 'agenda_pessoal';
}

/**
 * Verifica se o convite deve ser roteado para a secretaria da diretoria
 * ao invés de ir diretamente para o participante
 * @param {string[]} participantRoles - Papéis do participante
 * @returns {boolean}
 */
export function shouldRouteToSecretary(participantRoles) {
    return hasAnyRole(participantRoles, [ROLES.DIRETORIA]);
}

/**
 * Determina quem deve receber o convite baseado nas regras de negócio
 * @param {string[]} creatorRoles - Papéis do criador do evento
 * @param {Object} participant - Objeto do participante { uid, roles, ... }
 * @returns {Object} { shouldReceive: boolean, targetUid: string, reason: string }
 */
export function determineInviteRecipient(creatorRoles, participant) {
    const { id: participantUid, roles: participantRoles } = participant;

    // Regra: Se participante é da diretoria, convite vai para secretaria
    if (shouldRouteToSecretary(participantRoles)) {
        return {
            shouldReceive: false, // Participante não recebe diretamente
            routeToSecretary: true,
            reason: 'Participante é membro da diretoria, convite roteado para secretaria'
        };
    }

    // Regra 1: Auditor da Qualidade criando evento
    if (hasAnyRole(creatorRoles, [ROLES.AUDITOR_QUALIDADE])) {
        const eligibleRoles = [
            ROLES.GESTOR_PRONTOBABY,
            ROLES.GESTOR_HOMECARE,
            ROLES.COORDENADOR,
            ROLES.COLABORADOR,
            ROLES.ADMINISTRADOR
        ];

        if (hasAnyRole(participantRoles, eligibleRoles)) {
            return {
                shouldReceive: true,
                targetUid: participantUid,
                reason: 'Participante elegível para convite de auditor'
            };
        }

        return {
            shouldReceive: false,
            reason: 'Participante não tem papel elegível para convite de auditor'
        };
    }

    // Regra 2: Secretaria/Diretoria criando evento
    if (hasAnyRole(creatorRoles, [ROLES.SECRETARIA_DIRETORIA, ROLES.DIRETORIA])) {
        return {
            shouldReceive: true,
            targetUid: participantUid,
            reason: 'Secretaria/Diretoria pode convidar qualquer um'
        };
    }

    // Regra 3: Gestores/Coordenadores/Colaboradores criando evento
    const managerRoles = [
        ROLES.GESTOR_PRONTOBABY,
        ROLES.GESTOR_HOMECARE,
        ROLES.COORDENADOR,
        ROLES.COLABORADOR,
        ROLES.ADMINISTRADOR
    ];

    if (hasAnyRole(creatorRoles, managerRoles)) {
        if (hasAnyRole(participantRoles, managerRoles)) {
            return {
                shouldReceive: true,
                targetUid: participantUid,
                reason: 'Convite entre gestores/coordenadores/colaboradores'
            };
        }

        return {
            shouldReceive: false,
            reason: 'Participante não tem papel compatível'
        };
    }

    // Caso padrão: não deve receber
    return {
        shouldReceive: false,
        reason: 'Criador não tem permissão para criar convites'
    };
}

/**
 * Busca o UID da secretaria da diretoria
 * @returns {Promise<string|null>} UID da secretaria ou null
 */
export async function getSecretaryUid() {
    try {
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('roles', 'array-contains', ROLES.SECRETARIA_DIRETORIA));
        const snapshot = await getDocs(q);

        if (!snapshot.empty) {
            return snapshot.docs[0].id;
        }

        console.warn('Nenhuma secretaria da diretoria encontrada');
        return null;
    } catch (error) {
        console.error('Erro ao buscar secretaria:', error);
        return null;
    }
}

/**
 * Formata nome de papel para exibição
 * @param {string} roleId - ID do papel
 * @returns {string} Nome formatado
 */
export function formatRoleName(roleId) {
    const roleNames = {
        [ROLES.AUDITOR_QUALIDADE]: 'Auditor da Qualidade',
        [ROLES.GESTOR_PRONTOBABY]: 'Gestor ProntoBaby',
        [ROLES.GESTOR_HOMECARE]: 'Gestor Homecare',
        [ROLES.COORDENADOR]: 'Coordenador',
        [ROLES.COLABORADOR]: 'Colaborador',
        [ROLES.ADMINISTRADOR]: 'Administrador',
        [ROLES.DIRETORIA]: 'Diretoria',
        [ROLES.SECRETARIA_DIRETORIA]: 'Secretaria da Diretoria'
    };

    return roleNames[roleId] || roleId;
}
